import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import MaterialTable from 'material-table';
import tableIcons from "./components/MaterialTableIcons";
import { createTheme, makeStyles, ThemeProvider } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';

// Pop-up Dialog(Payment ID)
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import PaymentID from './PaymentID';
import { useHistory } from 'react-router-dom';
import { api_url } from "./authorize";
import axios from 'axios';
import { AuthorizeContext } from './RootContext';

// table Theme
const theme = createTheme({
  typography: { h6: { fontSize: '1.4em' } },
});

const tableStyles = makeStyles({
  root: { justifyContent: 'space-evenly' },
  spacing: { justifyContent: 'space-evenly' },
});

const Payable = () => {
  const [selectedRows, setSelectedRows] = useState([]);
  const { token, setAuthBody, setAuthenticated } = useContext(AuthorizeContext);

  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState({});

  const tableClass = tableStyles();

  const [allStubs, setStubs] = useState({
    unpaid: [],
    paid: [],
  });

  const [jobsByPaymentId, setJobsByPaymentId] = useState({});

  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("payableViewMode") || "unpaid";
  });

  const changeViewMode = useCallback((mode) => {
    localStorage.setItem("payableViewMode", mode);
    window.location.reload();
  }, []);

  const history = useHistory();

  const goBack = useCallback(() => {
    history.push('/accounthome');
  }, [history]);

  const stripJobsFromRows = useCallback((rows, statusLabel) => {
    return Array.isArray(rows)
      ? rows.map((row) => {
          const { jobs, ...cleanRow } = row;

          return {
            ...cleanRow,
            payment_status_label: statusLabel,
          };
        })
      : [];
  }, []);

  const buildJobsMap = useCallback((rows) => {
    const map = {};

    if (Array.isArray(rows)) {
      rows.forEach((row) => {
        if (row && row.payment_id) {
          map[row.payment_id] = Array.isArray(row.jobs) ? row.jobs : [];
        }
      });
    }

    return map;
  }, []);

  const handleClickOpen = useCallback((rowData) => () => {
    const safeRow = rowData || {};

    setChosen({
      ...safeRow,
      jobs: jobsByPaymentId[safeRow.payment_id] || [],
    });

    setOpen(true);
  }, [jobsByPaymentId]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setChosen({});
  }, []);

  const tableData = useMemo(() => {
    const unpaidRows = Array.isArray(allStubs.unpaid) ? allStubs.unpaid : [];
    const paidRows = Array.isArray(allStubs.paid) ? allStubs.paid : [];

    if (viewMode === "paid") {
      return paidRows;
    }

    if (viewMode === "all") {
      return [...unpaidRows, ...paidRows];
    }

    return unpaidRows;
  }, [viewMode, allStubs]);

  const fetchPaystubs = useCallback(async () => {
    try {
      const res = await axios({
        url: `https://${api_url}/api/paystubs`,
        method: 'get',
        headers: {
          'x-access-token': token,
        },
      });

      if (res.data.message) {
        alert(res.data.message);
      } else if (res.data.validation) {
        alert(res.data.validation.replace("_", " "));
      } else if (res.data.notLoggedIn) {
        setAuthenticated(false);
        setAuthBody({ role: 'visitor' });
        alert("You have been logged out due to inactivity. Please log in again.");
      } else {
        const unpaidRows = Array.isArray(res.data.unpaid) ? res.data.unpaid : [];
        const paidRows = Array.isArray(res.data.paid) ? res.data.paid : [];

        setJobsByPaymentId({
          ...buildJobsMap(unpaidRows),
          ...buildJobsMap(paidRows),
        });

        setStubs({
          unpaid: stripJobsFromRows(unpaidRows, "UNPAID"),
          paid: stripJobsFromRows(paidRows, "PAID"),
        });
      }
    } catch (error) {
      console.error("Failed to load payable data:", error);
      alert("Failed to load payable data.");
    }
  }, [
    token,
    setAuthenticated,
    setAuthBody,
    buildJobsMap,
    stripJobsFromRows,
  ]);

  useEffect(() => {
    fetchPaystubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const updatePaystubOnScreen = useCallback((updatedRow) => {
    const isPaid = Number(updatedRow.unpaid_amt || 0) <= 0;

    const updatedStatusRow = {
      ...updatedRow,
      payment_status_label: isPaid ? "PAID" : "UNPAID",
    };

    setStubs((prev) => {
      const unpaidRows = Array.isArray(prev.unpaid) ? prev.unpaid : [];
      const paidRows = Array.isArray(prev.paid) ? prev.paid : [];

      const unpaidWithoutCurrent = unpaidRows.filter(
        (row) => row.payment_id !== updatedRow.payment_id
      );

      const paidWithoutCurrent = paidRows.filter(
        (row) => row.payment_id !== updatedRow.payment_id
      );

      if (isPaid) {
        return {
          unpaid: unpaidWithoutCurrent,
          paid: [...paidWithoutCurrent, updatedStatusRow],
        };
      }

      return {
        unpaid: [...unpaidWithoutCurrent, updatedStatusRow],
        paid: paidWithoutCurrent,
      };
    });
  }, []);

  const editTheRow = useCallback(async (newRow) => {
    const res = await axios({
      url: `https://${api_url}/api/paystubs`,
      method: 'put',
      data: newRow,
      headers: {
        'x-access-token': token,
      },
    });

    if (res.data.validation) {
      alert(res.data.validation.replace("_", " "));
      return;
    }

    if (res.data.notLoggedIn) {
      setAuthenticated(false);
      setAuthBody({ role: 'visitor' });
      alert("You have been logged out due to inactivity. Please log in again.");
      return;
    }

    updatePaystubOnScreen(newRow);

    alert(res.data.message || "Paystub updated.");

    // This keeps the page synced with the backend.
    // The row already moves immediately, so the user does not need to refresh.
    fetchPaystubs();
  }, [
    token,
    setAuthenticated,
    setAuthBody,
    updatePaystubOnScreen,
    fetchPaystubs,
  ]);

  const editableConfig = useMemo(() => ({
    onRowUpdate: (newRow) => new Promise((resolve, reject) => {
      if (newRow.payment_status_label === "PAID") {
        alert("Paid Paystubs cannot be edited");
        resolve();
        return;
      }

      const paystubAmt = Number(newRow.paystub_amt || 0);
      const paidAmt = Number(newRow.paid_amt || 0);

      newRow.unpaid_amt = paystubAmt - paidAmt;

      editTheRow(newRow)
        .then(() => resolve())
        .catch((error) => {
          console.error("Failed to update payable row:", error);
          alert("Failed to update row.");
          reject(error);
        });
    }),
  }), [editTheRow]);

  const handleSelectionChange = useCallback((rows) => setSelectedRows(rows), []);

  const columns = useMemo(() => [
    {
      title: 'Status',
      field: 'payment_status_label',
      editable: 'never',
      render: rowData => (
        <div
          style={{
            background: rowData.payment_status_label === "PAID" ? "#3bb78f" : "red",
            color: "white",
            padding: "4px 10px",
            borderRadius: "2px",
            textAlign: "center",
            fontWeight: "bold",
            minWidth: "75px",
          }}
        >
          {rowData.payment_status_label === "PAID" ? "PAID" : "UNPAID"}
        </div>
      ),
      cellStyle: { fontSize: '1em' },
    },
    {
      title: 'Processor Name',
      field: 'processor_name',
      cellStyle: { fontSize: '1em' },
      editable: 'never',
    },
    {
      title: 'Payment ID #',
      field: 'payment_id',
      render: rowData => (
        <div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClickOpen(rowData)}
          >
            {rowData.payment_id}
          </Button>
        </div>
      ),
      cellStyle: { fontSize: '1em' },
      editable: 'never',
    },
    {
      title: 'Payment ID AMT',
      field: 'paystub_amt',
      cellStyle: { fontSize: '1em' },
      type: 'currency',
      editable: 'never',
    },
    {
      title: 'Current Amt',
      field: 'unpaid_amt',
      cellStyle: { fontSize: '1em' },
      type: 'currency',
      editable: 'never',
    },
    {
      title: 'Paid Amt',
      field: 'paid_amt',
      cellStyle: { fontSize: '1em' },
      type: 'currency',
    },
    {
      title: 'Pay Date',
      field: 'pay_date',
      type: 'date',
      cellStyle: { fontSize: '1em' },
    },
    {
      title: 'Ref',
      field: 'reference',
      cellStyle: { fontSize: '1em' },
    },
  ], [handleClickOpen]);

  const tableOptions = useMemo(() => ({
    sorting: true,
    search: true,
    searchFieldAlignment: "right",
    searchAutoFocus: false,
    searchFieldVariant: "outlined",

    filtering: false,
    paging: true,
    pageSizeOptions: [2, 5, 10, 15, 50],
    pageSize: 5,
    paginationType: "stepped",
    showFirstLastPageButtons: false,
    paginationPosition: "bottom",

    exportButton: false,
    exportAllData: false,
    columnsButton: false,

    addRowPosition: "first",
    actionsColumnIndex: -1,
    selection: false,
    showSelectAllCheckbox: false,
    showTextRowsSelected: false,
    grouping: false,

    rowStyle: (data, index) => index % 2 === 0 ? { background: "#f5f5f5" } : null,
    headerStyle: { background: "lightgray", fontStyle: "italic", fontSize: 19 },

    selectionProps: rowData => ({
      disabled: rowData.tax == null,
      color: "primary",
    }),
  }), []);

  const filterButtonStyle = (mode) => ({
    marginLeft: "6px",
    padding: "6px 12px",
    cursor: "pointer",
    background: viewMode === mode ? "#3f51b5" : "white",
    color: viewMode === mode ? "white" : "black",
    border: "1px solid #999",
    borderRadius: "3px",
  });

  return (
    <div className="payable">
      <h1 align="center">PAYABLE</h1>

      <button onClick={goBack}>
        Back
      </button>

      <button
        onClick={() => changeViewMode("unpaid")}
        style={filterButtonStyle("unpaid")}
      >
        UnPaid
      </button>

      <button
        onClick={() => changeViewMode("paid")}
        style={filterButtonStyle("paid")}
      >
        Paid
      </button>

      <button
        onClick={() => changeViewMode("all")}
        style={filterButtonStyle("all")}
      >
        Show All
      </button>

      <ThemeProvider theme={theme}>
        <MaterialTable
          title={
            viewMode === "all"
              ? "Accounts Payable - All"
              : viewMode === "paid"
                ? "Accounts Payable - Paid"
                : "Accounts Payable - Unpaid"
          }
          icons={tableIcons}
          columns={columns}
          data={tableData}
          editable={editableConfig}
          onSelectionChange={handleSelectionChange}
          options={tableOptions}
        />
      </ThemeProvider>

      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        aria-labelledby="form-dialog-title"
        color="primary"
      >
        <DialogTitle id="form-dialog-title">
          {/* Payment ID Detail */}
        </DialogTitle>

        <DialogContent>
          <div>
            {open && (
              <PaymentID
                data={chosen.jobs || []}
                name={chosen.processor_name || ""}
                id={chosen.payment_id || ""}
                sum={chosen.paystub_amt || 0}
              />
            )}
          </div>

          <DialogActions
            disableSpacing
            classes={{
              root: tableClass.root,
              spacing: tableClass.spacing,
            }}
          >
            <button
              className="btn btn-primary"
              onClick={handleClose}
              style={{ fontSize: '2em' }}
            >
              Close
            </button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payable;